/**
 * SVG fragment → HTMLCanvasElement rasterizer (Tier 2 Group F sub-piece
 * #3 — SVG import). Used for features the editor can't represent as
 * vector layers (filters, masks, gradients, text, etc.) — wrap the
 * fragment in a minimal `<svg>` shell, blob-URL it, draw to canvas
 * via an `<img>` element. The browser handles the rendering; we get
 * back ready-to-display pixels.
 *
 * Why this works: every browser's `<img>` element accepts SVG blob
 * URLs and renders them via the same SVG renderer as inline `<svg>`,
 * giving us full feature support (filters, gradients, text, masks,
 * clipPath, blend modes) for free. The alternatives are:
 *
 *  - Re-implementing SVG rendering in canvas2d. Months of work and
 *    we'd still be missing edge cases the browser handles.
 *  - Using `canvg` or `svg.js`. Adds ~200KB bundle + maintenance
 *    surface for a one-shot rasterization pass.
 *  - `XMLSerializer + drawImage(svgEl)` directly — works in some
 *    browsers but Chrome/Firefox have CORS quirks with embedded
 *    `<image>` refs that the blob-URL approach sidesteps.
 *
 * The function is async because `<img>.onload` fires asynchronously.
 * Returns a Promise so the caller can `await` the canvas.
 *
 * Caveats:
 *  - External `<image href="https://other-domain/foo.jpg">` refs make
 *    the rendered canvas tainted (cross-origin pixel data). Caller's
 *    `toBlob` / `getImageData` will throw `SecurityError`. We surface
 *    this as `SvgRasterizeError { kind: 'cors-tainted' }` so the
 *    import path can emit a clean warning and skip the layer.
 *  - `<script>` elements are stripped from the wrapped fragment before
 *    rasterization. Browsers don't execute scripts in `<img>`-rendered
 *    SVG, but stripping pre-render is belt-and-suspenders against any
 *    future spec change.
 *  - Very large SVGs (>10MB serialized, or >8192×8192 px) are rejected
 *    upfront — the `<img>` decoder allocates a single contiguous RGBA
 *    buffer; large fragments OOM the tab.
 */

const MAX_FRAGMENT_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_RASTER_DIMENSION = 8192; // pixels per side

export interface RasterizeOptions {
  /** SVG fragment (the content INSIDE the outer `<svg>` shell — we
   *  wrap it ourselves so the caller passes just the relevant
   *  subtree's serialization).
   *
   *  When the caller wants to rasterize a full `<svg>` document, pass
   *  the full document and the wrapping skips the redundant shell —
   *  see `wrapFragment` below. */
  fragment: string;
  /** Target canvas width (document pixels). Determines the rendered
   *  resolution of the rasterized fragment. */
  width: number;
  /** Target canvas height (document pixels). */
  height: number;
  /** The viewBox attribute the wrapping SVG should use, so the
   *  fragment's coordinates render at the expected position. Pass
   *  the full original document's viewBox so embedded fragments
   *  preserve their absolute placement.
   *
   *  Format: `"minX minY width height"` (SVG spec). When undefined,
   *  the wrapping SVG uses no viewBox and the fragment renders in
   *  its own coordinate system. */
  viewBox?: string;
  /** Optional `defs` block to inline into the wrapping SVG. SVG
   *  features like gradients and filters are typically declared in
   *  `<defs>` and referenced by id elsewhere; when rasterizing a
   *  fragment, the parser may need the defs to resolve those refs.
   *  Pass the full original document's `<defs>` block here. */
  defs?: string;
}

export type SvgRasterizeErrorKind =
  | 'too-large'
  | 'load-failed'
  | 'cors-tainted'
  | 'dimensions-exceed-limit';

/** Thrown when rasterization fails for a recoverable reason. The
 *  importer catches this, emits a flatten-warning, and skips the
 *  layer rather than failing the whole import. */
export class SvgRasterizeError extends Error {
  constructor(
    public readonly kind: SvgRasterizeErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'SvgRasterizeError';
  }
}

/** Wrap a fragment in a minimal `<svg>` shell so it can be served
 *  to an `<img>` element. If the fragment is already a full `<svg>`
 *  element (starts with `<svg`), it's used directly. Exported for
 *  unit testing — the rasterization step itself needs canvas2d
 *  which happy-dom doesn't ship. */
export function wrapFragment(opts: RasterizeOptions): string {
  const { fragment, width, height, viewBox, defs } = opts;
  const trimmed = fragment.trim();
  // Strip `<script>` elements defensively. Browsers don't execute
  // scripts in `<img>`-rendered SVG, but stripping pre-render is
  // belt-and-suspenders.
  const scrubbed = trimmed.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  if (scrubbed.startsWith('<svg')) {
    // Full document — use as-is but ensure xmlns is present (some
    // SVGs in the wild are missing it; `<img>` requires it).
    if (!scrubbed.includes('xmlns=')) {
      return scrubbed.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
    }
    return scrubbed;
  }
  // Bare fragment — wrap it.
  const viewBoxAttr = viewBox ? ` viewBox="${viewBox}"` : '';
  const defsBlock = defs ? `<defs>${defs}</defs>` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"${viewBoxAttr}>${defsBlock}${scrubbed}</svg>`;
}

/** Rasterize an SVG fragment to a fresh `HTMLCanvasElement` at the
 *  requested dimensions. Returns a Promise; rejects with
 *  `SvgRasterizeError` on a recoverable failure or with a generic
 *  Error on an unexpected one. */
export async function rasterizeSvgFragment(opts: RasterizeOptions): Promise<HTMLCanvasElement> {
  if (opts.width <= 0 || opts.height <= 0) {
    throw new SvgRasterizeError(
      'dimensions-exceed-limit',
      `Invalid rasterization dimensions: ${opts.width}×${opts.height}`,
    );
  }
  if (opts.width > MAX_RASTER_DIMENSION || opts.height > MAX_RASTER_DIMENSION) {
    throw new SvgRasterizeError(
      'dimensions-exceed-limit',
      `Rasterization dimensions ${opts.width}×${opts.height} exceed the ${MAX_RASTER_DIMENSION}px-per-side limit. Reduce SVG size or split into smaller layers in your source tool.`,
    );
  }

  const svgString = wrapFragment(opts);
  if (svgString.length > MAX_FRAGMENT_BYTES) {
    throw new SvgRasterizeError(
      'too-large',
      `SVG fragment is ${(svgString.length / 1024 / 1024).toFixed(1)} MB; the rasterizer caps fragments at ${MAX_FRAGMENT_BYTES / 1024 / 1024} MB. Simplify the SVG or split it in your source tool first.`,
    );
  }

  // Blob URL — `<img>.src = blobUrl` works without a network request,
  // unlike a data URL which would also work but base64-bloats large
  // SVGs by ~33% (matters for the 10MB cap). The URL is revoked
  // after load (success or fail) to free the bytes.
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);

  try {
    const img = await loadImage(blobUrl);
    const canvas = document.createElement('canvas');
    canvas.width = opts.width;
    canvas.height = opts.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new SvgRasterizeError(
        'load-failed',
        'Canvas 2D context unavailable — rasterization requires canvas2d support.',
      );
    }
    ctx.drawImage(img, 0, 0, opts.width, opts.height);
    // CORS detection: try `getImageData` on a single pixel. Tainted
    // canvases throw `SecurityError`; the layer is skipped with a
    // clean warning rather than corrupting downstream paths that
    // assume `toBlob` works. Narrow the catch to actual SecurityError
    // so other failures (canvas allocator hiccup, GPU lost, etc.)
    // surface as `load-failed` rather than misleading the user about
    // CORS. Pre-PR review finding (R1#5).
    try {
      ctx.getImageData(0, 0, 1, 1);
    } catch (err) {
      const isSecurityError =
        err instanceof DOMException &&
        (err.name === 'SecurityError' || err.code === DOMException.SECURITY_ERR);
      if (isSecurityError) {
        throw new SvgRasterizeError(
          'cors-tainted',
          'Rasterized canvas is CORS-tainted (external image references from a different origin). The pixels rendered correctly but cannot be saved back. Inline the referenced images in your source tool, or host them on the same origin.',
        );
      }
      throw new SvgRasterizeError(
        'load-failed',
        `Canvas readback failed after rasterization: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    }
    return canvas;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

/** Promise wrapper around `<img>.onload` / `onerror`. Crossorigin is
 *  set to `'anonymous'` so external `<image>` refs inside the SVG
 *  attempt CORS — if the response doesn't include the right header,
 *  the canvas taints but the load itself usually succeeds (the
 *  taint-detection in the caller handles that). When even the blob
 *  load fails (malformed SVG), `onerror` fires. */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      reject(
        new SvgRasterizeError(
          'load-failed',
          'SVG failed to load as an image — likely malformed XML or unsupported features. Open the file in a browser to confirm it renders.',
        ),
      );
    };
    img.src = src;
  });
}
